import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ReactSortable } from 'react-sortablejs'
import { useSWRConfig } from 'swr'
import Client from '../../models/client'
import { FETCH_KEYS, updateClientPrefectureOrder } from '../api'
import { BaseModal } from './BaseModal'
import { PREFECTURE_LIST } from './Global'
import { confirmDiscardChanges } from '../utils/confirm'

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
}

type Prefecture = { id: number; name: string }

const buildInitialList = (order: unknown): Prefecture[] => {
  if (!Array.isArray(order) || order.length !== PREFECTURE_LIST.length) {
    return [...PREFECTURE_LIST]
  }
  const indexMap = new Map(PREFECTURE_LIST.map((p) => [p.id, p]))
  const next: Prefecture[] = []
  for (const id of order) {
    const found = indexMap.get(Number(id))
    if (!found) return [...PREFECTURE_LIST]
    next.push(found)
  }
  if (new Set(next.map((p) => p.id)).size !== PREFECTURE_LIST.length) {
    return [...PREFECTURE_LIST]
  }
  return next
}

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid #0000001A',
  borderRadius: 8,
  padding: 4,
  backgroundColor: '#fafafa',
}

const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  margin: '2px 0',
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 4,
  cursor: 'grab',
  userSelect: 'none',
  fontSize: 14,
}

const handleStyle: React.CSSProperties = {
  color: '#999',
  marginRight: 10,
  fontSize: 18,
  lineHeight: 1,
}

const indexStyle: React.CSSProperties = {
  display: 'inline-block',
  width: 28,
  color: '#666',
  fontSize: 12,
}

export const PrefectureOrderModal: React.FC<Props> = ({ opened, onClose, client }) => {
  const { mutate } = useSWRConfig()
  const [baseline, setBaseline] = useState<Prefecture[]>(buildInitialList(client.prefecture_order))
  const [list, setList] = useState<Prefecture[]>(baseline)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (opened) {
      const initial = buildInitialList(client.prefecture_order)
      setBaseline(initial)
      setList(initial)
      setErrorMessage(null)
    }
  }, [opened, client.prefecture_order])

  const isDirty = useMemo(
    () => list.some((p, i) => p.id !== baseline[i]?.id),
    [list, baseline],
  )

  useEffect(() => {
    if (!opened || !isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [opened, isDirty])

  const handleSave = async () => {
    setSubmitting(true)
    setErrorMessage(null)
    try {
      await updateClientPrefectureOrder(
        client.id,
        list.map((p) => p.id),
      )
      mutate(FETCH_KEYS.fetchClient(client.id))
      setBaseline(list)
      notifications.show({ message: '保存しました', color: 'teal' })
      onClose()
    } catch (e: any) {
      const serverMsg = e?.response?.data?.prefecture_order?.[0]
      setErrorMessage(serverMsg || '保存に失敗しました。時間をおいて再度お試しください。')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResetToJis = () => {
    setList([...PREFECTURE_LIST])
  }

  const handleClose = () => {
    if (isDirty) {
      if (!confirmDiscardChanges()) {
        return
      }
    }
    setList(baseline)
    onClose()
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      width={560}
      title={
        <Group gap={12}>
          <Text fw={600}>都道府県表示順</Text>
          {isDirty && (
            <Text fz={12} c='#8a6d3b' bg='#fff3cd' style={{ border: '1px solid #f0c36d', borderRadius: 4, padding: '2px 8px' }}>
              ⚠ 未保存の変更があります
            </Text>
          )}
        </Group>
      }
    >
      <ScrollArea style={{ flex: 1, minHeight: 0 }} bg='#00000008'>
        <Stack gap='md' p='md'>
          <Text size='sm' c='dimmed'>
            紹介ページ・アンケートで表示される都道府県の並び順をドラッグ&ドロップで変更できます。
            <br />
            「JIS順にリセット」を押すと初期順（北海道→沖縄→海外）に戻ります。
          </Text>

          <Box>
            <ReactSortable
              list={list}
              setList={setList}
              animation={150}
              style={listStyle}
            >
              {list.map((pref, i) => (
                <div key={pref.id} style={itemStyle}>
                  <span style={handleStyle}>≡</span>
                  <span style={indexStyle}>{i + 1}.</span>
                  <span>{pref.name}</span>
                </div>
              ))}
            </ReactSortable>
          </Box>

          {errorMessage && (
            <Text c='red' size='sm'>{errorMessage}</Text>
          )}
        </Stack>
      </ScrollArea>

      <Box
        p='md'
        style={{
          borderTop: '1px solid #0000001A',
          backgroundColor: '#fff',
          flexShrink: 0,
        }}
      >
        <Group justify='space-between'>
          <Button
            variant='outline'
            radius='xl'
            color='gray'
            onClick={handleResetToJis}
            disabled={submitting}
          >
            JIS順にリセット
          </Button>
          <Group gap={8}>
            <Button
              variant='outline'
              radius='xl'
              color='gray'
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant='filled'
              radius='xl'
              onClick={handleSave}
              loading={submitting}
              disabled={!isDirty}
            >
              保存
            </Button>
          </Group>
        </Group>
      </Box>
    </BaseModal>
  )
}
